import { Search, Plus } from "lucide-react";
import type { Entity, JobStatus } from "@/lib/mockData";

interface Props {
  entities: Entity[];
  entityFilter: string;
  onEntityFilter: (v: string) => void;
  statusFilter: JobStatus | "all";
  onStatusFilter: (v: JobStatus | "all") => void;
  statusCounts: Partial<Record<JobStatus, number>>;
  search: string;
  onSearch: (v: string) => void;
  tileSize: "s" | "m" | "l";
  onTileSize: (v: "s" | "m" | "l") => void;
  onQueueJob?: () => void;
}

const STATUS_OPTIONS: (JobStatus | "all")[] = [
  "all",
  "running",
  "approval",
  "takeover",
  "failed",
  "success",
  "queued",
];

const STATUS_LABEL: Record<JobStatus | "all", string> = {
  all: "All",
  running: "Running",
  approval: "Approval",
  takeover: "Takeover",
  failed: "Failed",
  success: "Done",
  human: "Human",
  queued: "Queued",
};

export function FilterBar({
  entities,
  entityFilter,
  onEntityFilter,
  statusFilter,
  onStatusFilter,
  statusCounts,
  search,
  onSearch,
  tileSize,
  onTileSize,
  onQueueJob,
}: Props) {
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-paper-line bg-paper-1 px-6 py-3">
      <Pills
        options={[{ value: "all", label: "All" }, ...entities.map((e) => ({ value: e.id, label: e.name }))]}
        value={entityFilter}
        onChange={onEntityFilter}
      />

      <span className="h-6 w-px bg-paper-line" aria-hidden />

      <Pills
        options={STATUS_OPTIONS.map((s) => ({
          value: s,
          label: STATUS_LABEL[s],
          count: s === "all" ? undefined : statusCounts[s as JobStatus],
        }))}
        value={statusFilter}
        onChange={(v) => onStatusFilter(v as JobStatus | "all")}
      />

      <label className="ml-auto flex items-center gap-2 rounded-sm border border-paper-line-strong bg-paper-3 px-2 py-1">
        <Search size={14} className="text-ink-4" aria-hidden />
        <input
          type="search"
          value={search}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search jobs, patients, workflows…"
          className="w-[260px] bg-transparent text-sm text-ink-1 placeholder:text-ink-4 focus:outline-none"
        />
      </label>

      <div className="flex items-center gap-0.5 rounded-sm border border-paper-line-strong bg-paper-3 p-0.5">
        {(["s", "m", "l"] as const).map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => onTileSize(s)}
            className={[
              "rounded-xs px-2 py-1 text-xs font-medium transition-colors",
              tileSize === s ? "bg-paper-tint text-ink-1" : "text-ink-3 hover:bg-paper-tint",
            ].join(" ")}
          >
            {s.toUpperCase()}
          </button>
        ))}
      </div>

      <button
        type="button"
        onClick={onQueueJob}
        className="flex items-center gap-1 rounded-sm border border-teal-600 bg-teal-500 px-3 py-1.5 text-sm text-paper-3 transition-colors hover:bg-teal-600"
      >
        <Plus size={14} aria-hidden />
        Queue job
      </button>
    </div>
  );
}

function Pills({
  options,
  value,
  onChange,
}: {
  options: { value: string; label: string; count?: number }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-1">
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            className={[
              "flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs transition-colors",
              active
                ? "border border-teal-100 bg-teal-50 text-teal-700"
                : "border border-transparent text-ink-3 hover:bg-paper-tint",
            ].join(" ")}
          >
            <span>{o.label}</span>
            {typeof o.count === "number" && (
              <span className="font-mono text-[10px] text-ink-4">{o.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}
